import { app } from '@/firebase'
import { isProd, usingEmulators } from './analyticsEnv'

/**
 * Firebase Analytics (GA4) event tracking. This is the ONLY module allowed to
 * import `firebase/analytics` — every call site elsewhere in the app just
 * calls `track(...)` and never touches the SDK directly.
 *
 * Enablement — analytics only initializes (and `track` only sends events)
 * when ALL of the following hold:
 *   - this is a production build (`import.meta.env.PROD === true`)
 *   - the app is NOT pointed at the emulator suite
 *     (`VITE_USE_FIREBASE_EMULATORS !== 'true'`)
 *   - the browser supports the SDK (`firebase/analytics`'s `isSupported()`
 *     resolves `true`)
 * Otherwise every `track` call is a silent no-op. This guarantees dev
 * servers, Jest, and the emulator-backed Playwright/golden suites never send
 * real GA events, without any caller needing to know or care.
 *
 * `track` is fire-and-forget by design: callers never `await` it, and it must
 * never throw — every failure mode (unsupported browser, blocked network,
 * import failure, whatever) degrades to a no-op, at most logged via
 * `console.debug`.
 *
 * Event names/params follow GA4 conventions: event names are short snake_case
 * nouns/verbs (`tag_view`, `post_create`, ...), and params are always strings
 * or numbers (GA4 does not accept nested objects/arrays).
 *
 * No manual page-view tracking: GA4's "enhanced measurement" already
 * auto-collects `page_view` and `session_start` for the web SDK once
 * analytics is initialized, so there is intentionally no router-level
 * tracking call here.
 */

export type AnalyticsEvent =
  | 'tag_view'
  | 'post_create'
  | 'sign_up'
  | 'login'
  | 'tag_claim'
  | 'location_set'
  | 'reaction_add'
  | 'feedback_send'

type EventParams = Record<string, string | number>

type LogEventFn = (name: string, params?: EventParams) => void

/** Caps the pre-init buffer so a runaway caller can't leak memory. */
const MAX_BUFFER = 20

/** null = decide from `isProd()`/`usingEmulators()`; set by `_resetForTest`. */
let enabledOverride: boolean | null = null

type InitState = 'idle' | 'initializing' | 'ready' | 'unavailable'
let state: InitState = 'idle'
let logEventFn: LogEventFn | null = null
let pending: { event: AnalyticsEvent; params?: EventParams }[] = []

const shouldAttemptInit = (): boolean =>
  enabledOverride ?? (isProd() && !usingEmulators())

/** Kicks off (once) the dynamic import + support check + `getAnalytics`. */
const startInit = (): void => {
  state = 'initializing'
  void (async () => {
    try {
      const mod = await import('firebase/analytics')
      const supported = await mod.isSupported()
      if (!supported) {
        state = 'unavailable'
        pending = []
        return
      }
      const analytics = mod.getAnalytics(app)
      logEventFn = (name, params) => mod.logEvent(analytics, name, params)
      state = 'ready'
      const queued = pending
      pending = []
      for (const item of queued) {
        try {
          logEventFn(item.event, item.params)
        } catch (err) {
          console.debug('[analytics] flush failed', err)
        }
      }
    } catch (err) {
      state = 'unavailable'
      pending = []
      console.debug('[analytics] init failed', err)
    }
  })()
}

/**
 * Fire an analytics event. Fire-and-forget — never throws, never awaited by
 * callers. No-ops entirely when analytics is disabled (see enablement rules
 * above). Events fired while init is still in flight are buffered (capped)
 * and flushed once init resolves, so the very first event on a cold load
 * isn't lost.
 */
export const track = (event: AnalyticsEvent, params?: EventParams): void => {
  try {
    if (!shouldAttemptInit()) return
    if (state === 'idle') startInit()
    if (state === 'ready' && logEventFn) {
      logEventFn(event, params)
      return
    }
    if (state === 'initializing' && pending.length < MAX_BUFFER) {
      pending.push({ event, params })
    }
    // state === 'unavailable': drop silently.
  } catch (err) {
    try {
      console.debug('[analytics] track failed', err)
    } catch {
      /* even console can't throw its way out of a fire-and-forget call */
    }
  }
}

/**
 * Test-only escape hatch: resets the lazy-singleton state and optionally
 * forces the enablement decision, so tests never have to fight
 * `import.meta.env`. Not used (and not needed) outside `__tests__`.
 */
export const _resetForTest = (overrides?: { enabled?: boolean }): void => {
  state = 'idle'
  logEventFn = null
  pending = []
  enabledOverride = overrides?.enabled ?? null
}
